import { useState, useEffect } from 'react';
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  TextField, Button, Snackbar, Alert, CircularProgress, Typography, Box
} from '@mui/material';
import { motion } from "framer-motion";
import logo from "../assets/images/leaf.png";
import chennaiLogo from "../assets/images/stsnlogo.png";
import { REST_URI } from '../graphql/client';
import { districts } from '../utils/formDataUtils';

const LoginPage = () => {
  const [mobile, setMobile] = useState('');
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [resendDisabled, setResendDisabled] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [message, setMessage] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarSeverity, setSnackbarSeverity] = useState('info');
  const [isLoading, setIsLoading] = useState(false);
  const [district, setDistrict] = useState('');
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [showDistrictSelection, setShowDistrictSelection] = useState(true);
  const navigate = useNavigate();
  const { district: currentDistrict } = useParams();
  const { login } = useAuth();

  useEffect(() => {
    if (currentDistrict) {
      setDistrict(currentDistrict);
    }
  }, [currentDistrict]);

  useEffect(() => {
    if (!resendDisabled) return;
    let timer;
    if (countdown > 0) {
      timer = setTimeout(() => setCountdown((prev) => prev - 1), 1000);
    } else {
      setResendDisabled(false);
    }
    return () => clearTimeout(timer);
  }, [countdown, resendDisabled]);

  const startResendTimer = () => {
    setResendDisabled(true);
    setCountdown(300);
  };

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60).toString().padStart(2, '0');
    const s = (seconds % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  const handleDistrictSelect = (selectedDistrict) => {
    setDistrict(selectedDistrict);
    setOtpSent(false);
    setMobile('');
    setOtp('');
    if (selectedDistrict === "Chennai") {
      setShowDistrictSelection(false);
    }
    navigate(`/${selectedDistrict}`);
  };

  const handleSendOtp = async () => {
    if (!district) {
      setMessage("Please select your district.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }
    if (mobile.length !== 10) {
      setMessage("Please enter a valid 10-digit mobile number.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    setOtp('');
    setIsSendingOtp(true);
    try {
      const response = await fetch(`${REST_URI}/send-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mobile, district }),
      });

      const data = await response.json();
      if (data.status === 'error') {
        setMessage(data.message || "Failed to send OTP.");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
        return;
      }

      setMessage("OTP sent successfully!");
      setSnackbarSeverity("success");
      setOtpSent(true);
      startResendTimer();

    } catch (error) {
      setMessage(error.message || "Failed to send OTP.");
      setSnackbarSeverity("error");
    } finally {
      setIsSendingOtp(false);
    }
    setSnackbarOpen(true);
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();

    if (otp.length !== 6) {
      setMessage("Please enter a valid 6-digit OTP.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(`${REST_URI}/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mobile, otp, district }),
      });

      const data = await response.json();
      if (data.status === 'error') {
        setMessage(data.message || "OTP verification failed.");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
        return;
      }

      login({
        id: data.user?.id,
        mobile,
        district,
        role: data.user.role,
      });
      localStorage.setItem("mobile", mobile);
      localStorage.setItem("district", district);
      localStorage.setItem("user_id", data.user?.id);
      localStorage.setItem("user_type", data.user.role);
      localStorage.setItem("first_name", data.user.First_Name);
      localStorage.setItem("isAdmin", data.user.isAdmin);
      localStorage.setItem("id", data.user.id);
      setMessage("Login Successful!");
      setSnackbarSeverity("success");
      navigate(`/${district}/membership`);
      window.scrollTo(0, 0);

    } catch (error) {
      setMessage(error.message || "OTP verification failed.");
      setSnackbarSeverity("error");
    } finally {
      setIsLoading(false);
    }
    setSnackbarOpen(true);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-yellow-100 via-yellow-200 to-yellow-300 p-6">
      {showDistrictSelection ? (
        <div className="w-full max-w-5xl flex flex-col items-center">
          <Typography variant="h4" sx={{ fontWeight: "600", color: 'black', mb: 6, textAlign: "center" }}>
            Select Your District
          </Typography>

          {/* ✅ Fixed grid section with equal height cards */}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 w-full max-h-[70vh] overflow-y-auto">
            {districts.map((dist) => (
              <motion.div
                key={dist}
                whileHover={{ scale: dist === "Chennai" ? 1.05 : 1 }}
                whileTap={{ scale: dist === "Chennai" ? 0.95 : 1 }}
                className="cursor-pointer h-full"
                onClick={() => handleDistrictSelect(dist)}
              >
                <div
                  className={`flex flex-col justify-center items-center h-full bg-white/70 rounded-xl py-4 px-3 text-center shadow-md hover:shadow-lg transition border-2 ${
                    district === dist ? 'border-yellow-700 bg-yellow-100' : 'border-yellow-500/30'
                  }`}
                >
                  <Typography
                    variant="body1"
                    className={`font-medium ${
                      district === dist ? 'text-yellow-800' : 'text-gray-700'
                    }`}
                  >
                    {dist}
                  </Typography>
                  {dist !== "Chennai" && (
                    <Typography variant="caption" sx={{ color: 'lightgrey', fontWeight: 'bold', mt: 1, display: "block" }}>
                      Work in Progress!
                    </Typography>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      ) : (
        <div className="w-full max-w-md">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div
              className="backdrop-blur-md bg-white/70 border border-yellow-600 shadow-2xl rounded-3xl p-8 relative"
            >
              <div className="absolute -top-5 left-1/2 transform -translate-x-1/2 bg-yellow-500 text-white rounded-full px-4 py-1 text-sm font-bold shadow-lg">
                Login
              </div>

              <div className="flex flex-col items-center mb-6 text-center">
                <img
                  src={district === 'Chennai' ? chennaiLogo : logo}
                  alt="Logo"
                  className="w-28 h-28 mb-4"
                />
                <Typography variant="h5" sx={{ fontWeight: "600" }}>
                  Welcome to {district} District Senaithalaivar Sangam!
                </Typography>
                <Typography sx={{ mt: 1, fontSize: 14 }}>
                  Sign in with your mobile number
                </Typography>
              </div>

              <form onSubmit={handleVerifyOtp} className="flex flex-col gap-4">
                <TextField
                  label="Mobile Number"
                  type="tel"
                  inputMode="numeric"
                  fullWidth
                  size="small"
                  value={mobile}
                  onChange={(e) => setMobile(e.target.value.replace(/\D/g, "").slice(0, 10))}
                  required
                  disabled={!district || otpSent}
                  placeholder="Enter 10-digit number"
                />

                {!otpSent ? (
                  <Button
                    variant="contained"
                    onClick={handleSendOtp}
                    disabled={isSendingOtp || mobile.length !== 10 || !district}
                    fullWidth
                    sx={{
                      background: "linear-gradient(135deg, #B4AB57, #947F49)",
                      fontWeight: "bold",
                      borderRadius: "10px",
                      py: 1.5,
                      "&:hover": {
                        background: "linear-gradient(135deg, #947F49, #B4AB57)",
                      },
                    }}
                  >
                    {isSendingOtp ? (
                      <CircularProgress size={24} sx={{ color: "#fff" }} />
                    ) : (
                      "Send OTP"
                    )}
                  </Button>
                ) : (
                  <>
                    <TextField
                      label="OTP"
                      type="tel"
                      inputMode="numeric"
                      fullWidth
                      size="small"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                      required
                      placeholder="Enter 6-digit OTP"
                    />

                    <Box sx={{ display: "flex", gap: 2 }}>
                      <Button
                        variant="outlined"
                        onClick={handleSendOtp}
                        disabled={resendDisabled}
                        sx={{ flex: 1, borderRadius: "10px", py: 1.5 }}
                      >
                        {resendDisabled ? formatTime(countdown) : "Resend OTP"}
                      </Button>

                      <Button
                        type="submit"
                        variant="contained"
                        disabled={isLoading || otp.length !== 6}
                        sx={{
                          flex: 2,
                          background: "linear-gradient(135deg, #B4AB57, #947F49)",
                          fontWeight: "bold",
                          borderRadius: "10px",
                          py: 1.5,
                          "&:hover": {
                            background: "linear-gradient(135deg, #947F49, #B4AB57)",
                          },
                        }}
                      >
                        {isLoading ? (
                          <CircularProgress size={24} sx={{ color: "#fff" }} />
                        ) : (
                          "Verify & Login"
                        )}
                      </Button>
                    </Box>
                  </>
                )}
              </form>
            </div>
          </motion.div>
        </div>
      )}

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={5000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} sx={{ width: "100%" }}>
          {message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default LoginPage;
